import { ENTITY_LABELS } from "../../../../utils/labelMapping";
import { Button, Modal, TableCell, TableHead, TableRow, Tooltip } from "@mui/material";

import Table, {
  StyledTableCell,
  StyledTableRow,
} from "./../../../../Component/UI/Table/Table";
import MUICard from "./../../../../Component/UI/MUICard/MUICard";
import classes from "./CoreAttributes.module.css";
import { extractDomain } from "../../../../utils/helpers";
import { faSortUp, faSortDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import EmptyView from "../../../../Component/EmptyView/EmptyView";
import Spinner from "../../../../Component/Spinner/Spinner";
import AIGenerateButton from "../../../../Component/AIGenerateButton/AIGenerateButton";
import { metricExplainations } from "../../../../utils/constant";
import MUIToolTip from "../../../../Component/UI/ActionTooltip/MUIToolTip";
import {
  createActionMetric,
  fetchActionMetrics,
} from "../../../../redux/actions/metricActions";
import { useDispatch } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useState } from "react";
import { getSourcesWithAlias } from "../../../../services/apiService";
import { createDomainUrlMapping } from "../ActionComponent/ActionModal";
import DetailedTableModal from "../../common/MetricDetails";
import MUIIconButton from "../../../../Component/UI/MUIIconButton/MUIIconButton";
import { InfoOutlined } from "@mui/icons-material";
import { PaginatedCoreAttributesTable } from "./PaginatedCoreAttributesTable";

const TABLE_HEADERS = [
  {
    id: "attr",
    name: `${ENTITY_LABELS.Brand} Pillars`,
  },
];

export function compareScores(currentData, previousData) {
  // Helper function to find score by brand name in a list
  const findBrandScore = (scores, brandName) => {
    return scores.find((scoreObj) => scoreObj.name === brandName);
  };

  return currentData?.map((category) => {
    // Find corresponding category in previous data
    const prevCategory = previousData?.find(
      (prev) => prev.name === category.name
    );

    // Map through each score in the current category
    const updatedScores = category.scores.map((currentScore) => {
      // Find previous score for the same brand (if exists)
      const prevScoreObj = prevCategory
        ? findBrandScore(prevCategory.scores, currentScore.name)
        : null;

      // Calculate delta
      const prevScore = prevScoreObj ? prevScoreObj.score : null;
      const delta =
        prevScore !== null && prevScore !== 0
          ? currentScore.score - prevScore
          : null;

      // Add delta to current score object
      return {
        ...currentScore,
        delta: delta,
      };
    });

    // Return updated category with scores containing delta
    return {
      ...category,
      scores: updatedScores,
    };
  });
}

export function formatName(str) {
  return str
    .split(" ") // Split the string into words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize each word
    .join(" ");
}

export function sourceSplitToDomain(source_links) {
  const groupedLinks =
    source_links?.filter(Boolean).reduce((acc, url) => {
      const domain = extractDomain(url);
      if (domain) {
        (acc[domain] ||= []).push(url);
      }
      return acc;
    }, {}) ?? {};

  if (Object.entries(groupedLinks) && Object.entries(groupedLinks).length) {
    return Object.entries(groupedLinks)?.map(([domain, urls], index) => ({
      domain_url: domain,
      fullUrl: urls || "",
    }));
  }
}

export default function CoreAttributes({
  coreAttributesData,
  pastcoreAttributesData,
  brandDomains,
  loading,
  errors,
  summaries,
  getAttributesSummary,
  selectedInput,
  sources,
  customerId,
  sourceURLIds,
  fetchRealTimeActionsSources,
  isChecked
}) {
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const dispatch = useDispatch();
  const [keyPressed, setKeyPressed] = useState(false);

  // Inside CoreAttributes component, add this state after existing useState calls:
  const [showReportingModal, setShowReportingModal] = useState(false);

  // Add this function to check reporting mode (add after existing functions like formatName, sourceSplitToDomain):
  function isReportingMode() {
    const isReporting =
      new URLSearchParams(window.location.search).get("isReporting") === "true" ||
      new URLSearchParams(window.location.search).get("reporting") === "true";
    return isReporting;
  }

  const handleClickI = (e) => {
    e.shiftKey && setKeyPressed("shiftKey");
    e.altKey && setKeyPressed("altKey");
    setShowModal(true);
  };

  const saveActions = async (title, rationale, realSource) => {
    setIsActionLoading(true);
    realSource = sourceSplitToDomain(realSource);
    const actionSources = Object.entries(Object.values(sources || {})?.[0] || {})?.map(
      ([domain_url, { aliases = [] }]) => ({
        domain_url,
        fullUrl: Array.from(new Set(aliases.map((item) => item?.source_alias_url)))?.filter(Boolean),
      })
    );
    const finalSource =
      realSource && realSource.length > 0
        ? realSource
        : actionSources
    const modifiedObj = {
      title: title,
      rationale: rationale,
      brand: selectedInput?.brand || selectedInput?.product,
      persona: selectedInput?.persona,
      geography: selectedInput?.geography,
      market: selectedInput?.market,
      category: selectedInput?.category,
      llm: selectedInput?.llm,
      isProduct: selectedInput?.product ? true : false,
      sources: finalSource,
    };

    const payload = {
      action_json: [modifiedObj],
      is_archived: 0,
      is_active: 1,
    };

    dispatch(createActionMetric(customerId, payload))
      .then(() => {
        dispatch(fetchActionMetrics(customerId));
        setIsActionLoading(false);
        // navigate("/luminaire/actions?actions_list=1");
        toast.success("Action Added!", {
          autoClose: 700,
        });
      })
      .catch(() => {
        setIsActionLoading(false);
        toast.error("Something went wrong!", {
          autoClose: 700,
        });
      });
  };

  function extractUniqueBrands(data) {
    if (data && data.length) {
      // Find the attribute with the maximum number of competitors (brands)
      let maxCompetitorAttribute = data.reduce((maxAttr, currentAttr) => {
        return currentAttr.scores.length > maxAttr.scores.length
          ? currentAttr
          : maxAttr;
      });

      // Get the unique brand names from that attribute
      const uniqueBrands = new Set();
      maxCompetitorAttribute.scores.forEach((brandObj) => {
        uniqueBrands.add(brandObj.name);
      });

      // Create an array of objects with unique id and brand name
      const brandArray = Array.from(uniqueBrands).map((brandName, index) => ({
        id: index + 1, // Unique ID starting from 1
        name: brandName,
      }));

      return brandArray;
    }
  }

  function renderRows(mainItem, index) {
    const attName = formatName(mainItem.name);
    return (
      <StyledTableRow key={index}>
        <Tooltip title={metricExplainations?.[attName]}>
          <StyledTableCell className={classes.attribute_headings}>
            {formatName(mainItem.name)}
          </StyledTableCell>
        </Tooltip>

        {mainItem.scores.map((item, index) => {
          let color = "#dc3545";
          if (item.score >= 7) color = "#2196F3";
          if (item.score >= 9) color = "#198754";

          const isCustomTooltip =
            (item?.name === (selectedInput?.brand || selectedInput?.product)) && selectedInput?.llm?.toLowerCase().includes("search")

          const TooltipComponent = isCustomTooltip ? MUIToolTip : Tooltip;

          const tooltipProps = isCustomTooltip
            ? {
              sourceURLIds,
              metric: `${ENTITY_LABELS.Brand} Pillars`,
              realtimeSources: fetchRealTimeActionsSources,
              attribute: formatName(mainItem.name),
              metricScore: Number(item.score).toFixed(0),
              selectedInput,
              sources,
              key: index,
              addToActionHandler: saveActions,
              actionText: "Add To Action",
              title: item.explanation,
              isActionLoading: isActionLoading,
              arrow: true,
              isBrandOverview: true,
              slotProps: {
                popper: {
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [0, -18],
                      },
                    },
                  ],
                },
              },
            }
            : {
              title: item.explanation,
              arrow: true,
              slotProps: {
                popper: {
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [0, -18],
                      },
                    },
                  ],
                },
              },
            };

          return (
            <TooltipComponent {...tooltipProps}>
              <StyledTableCell
                style={{ color }}
                className={classes.attribute_score}
              >
                {Number(item.score).toFixed(0)}
                &nbsp; &nbsp; {isChecked && item?.delta !== null && (
                  <span style={{ color: "grey" }}>
                    <FontAwesomeIcon
                      className={
                        item.delta < 0
                          ? `${classes["core-down-icon"]} text-danger me-1`
                          : `${classes["core-up-icon"]} text-success me-1`
                      }
                      icon={item.delta < 0 ? faSortDown : faSortUp}
                    />
                    <span className={classes.coreDelta}>
                      {Number(Math.abs(item.delta)).toFixed(0)}
                    </span>
                  </span>
                )}
              </StyledTableCell>
            </TooltipComponent>
          );
        })}
      </StyledTableRow>
    );
  }

  function renderHeaders() {
    const headers = [
      ...TABLE_HEADERS,
      ...extractUniqueBrands(coreAttributesData),
    ];

    return (
      <TableHead className={classes.table_head}>
        <TableRow>
          {headers.map((header, index) => {
            let domain = extractDomain(brandDomains?.[header?.name]);

            return (
              <TableCell key={index} className={classes.table_head_cell}>
                <div className={classes.table_head_content}>
                  {index != 0 && (
                    <img
                      data-html2canvas-ignore
                      className={classes.table_head_logo}
                      src={`https://img.logo.dev/${domain}?token=pk_SaDf4t2eRsW5Fc84zgJRZA`}
                    />
                  )}
                  <div
                    className={
                      index === 0
                        ? classes.table_head_text_comp
                        : classes.table_head_text
                    }
                  >
                    <div className={classes.table_head_title}>
                      <span>
                        <Tooltip title={metricExplainations[header.name]}>
                          {header.name}
                        </Tooltip>
                      </span>
                      {index === 0 && !showModal && (
                        <MUIIconButton onClick={handleClickI}>
                          <InfoOutlined />
                        </MUIIconButton>
                      )}
                    </div>
                  </div>
                </div>
              </TableCell>
            );
          })}
        </TableRow>
      </TableHead>
    );
  }

  const coreAttributesDataWithDelta = compareScores(
    coreAttributesData,
    pastcoreAttributesData
  );

  return (
    <>
      <MUICard variant="elevated" elevation={0} className={classes.card}>
        {coreAttributesDataWithDelta &&
          coreAttributesDataWithDelta.length > 0 ? (
          <>

              {isReportingMode() && (
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "12px" }}>
                  <Button
                    variant="outlined"
                    size="small"
                    // startIcon={<DescriptionIcon />}
                    onClick={() => setShowReportingModal(true)}
                    style={{ textTransform: "none" }}
                  >
                    Brand Pillars Report View
                  </Button>
                </div>
              )}
            <Table
              elevation={0}
              data={coreAttributesDataWithDelta}
              renderHeaders={() => renderHeaders(coreAttributesData)}
              render={renderRows}
            />

              <Modal
                handleClose={() => setShowReportingModal(false)}
                open={showReportingModal}
                title={`Brand Pillars - Report View (Paginated for PPT)`}
                sx={{
                  maxWidth: "90vw",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center"
                }}
                onClose={() => setShowReportingModal(false)}
              >
                <div style={{
                  maxHeight: "80vh",
                  overflowY: "auto",
                  padding: "8px",
                  width: "100%"
                }}>
                  <PaginatedCoreAttributesTable
                    data={coreAttributesDataWithDelta}
                    uniqueBrands={extractUniqueBrands(coreAttributesData)}
                    brandDomains={brandDomains}
                    styleClasses={classes}
                    formatName={formatName}
                    isChecked={isChecked}
                    maxBrandsPerTable={6}
                  />
                </div>
              </Modal>

            <DetailedTableModal
              show={showModal}
              onClose={() => {
                setShowModal(false);
                setKeyPressed(false);
              }}
              data={coreAttributesDataWithDelta}
              headers={extractUniqueBrands(coreAttributesData)?.map(
                (item) => item?.name
              )}
              renderHeaders={renderHeaders}
              formatName={formatName}
              classes={classes}
              pdfHeight={400}
              pdfTitle={`${selectedInput?.brand || selectedInput?.product
                }-Brand Pillars`}
              showSplitTables={keyPressed}
            />
          </>
        ) : (
          <>
            <h5>
              <Tooltip title={metricExplainations[`Brand Pillars`]}>
                Brand Pillars
              </Tooltip>
            </h5>
            <EmptyView message="Data Not Available" />
          </>
        )}
      </MUICard>

      <div className="col-md-12">
        <div className="graphGreyBg">
          <p className="mb-3 font14-600">
            Summary &nbsp; &nbsp;
            <AIGenerateButton
              fetchSummary={getAttributesSummary}
              type="brand-pillars"
            />
          </p>
          <div className="graphsummary font-11">
            {loading?.brandPillars ? (
              <Spinner />
            ) : summaries?.brandPillars ? (
              <p className="mb-1"> {summaries?.brandPillars} </p>
            ) : errors?.brandPillars ? (
              <p style={{ color: "red", textAlign: "center" }}>
                {errors?.brandPillars}
              </p>
            ) : null}
          </div>
        </div>
      </div>
      <ToastContainer />
    </>
  );
}

